import { useId } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import type { SurveyOption } from "./types";

interface MultiChoiceQuestionProps {
  question: string;
  hint?: string;
  options: SurveyOption[];
  values: string[];
  onChange: (values: string[]) => void;
  /** Deselects every other option when picked, and is deselected by any of them. */
  exclusiveValue?: string;
  error?: string;
}

/**
 * One multiple-answer question.
 *
 * A real `<fieldset>`/`<legend>`: native grouping is what screen readers handle best for a
 * set of checkboxes, and axe's WCAG rules will not flag its absence if we get it wrong.
 * Each Checkbox is a `<button>`, so options are named via `aria-labelledby` — see
 * `ChoiceQuestion` for why `<label htmlFor>` is not enough here.
 */
export function MultiChoiceQuestion({
  question,
  hint,
  options,
  values,
  onChange,
  exclusiveValue,
  error,
}: MultiChoiceQuestionProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const toggle = (value: string, checked: boolean) => {
    if (!checked) {
      onChange(values.filter((current) => current !== value));
      return;
    }
    if (value === exclusiveValue) {
      onChange([value]);
      return;
    }
    onChange([
      ...values.filter((current) => current !== exclusiveValue),
      value,
    ]);
  };

  return (
    <fieldset
      className="flex flex-col gap-2"
      aria-describedby={
        [hint ? hintId : null, error ? errorId : null]
          .filter(Boolean)
          .join(" ") || undefined
      }
    >
      <legend className="text-sm font-medium">{question}</legend>
      {hint && (
        <p id={hintId} className="text-sm text-muted-foreground">
          {hint}
        </p>
      )}
      <div className="grid gap-2 sm:grid-cols-2">
        {options.map((option) => {
          const optionId = `${id}-${option.value}`;
          return (
            <div key={option.value} className="flex items-center gap-2">
              <Checkbox
                id={optionId}
                checked={values.includes(option.value)}
                onCheckedChange={(checked) =>
                  toggle(option.value, checked === true)
                }
                aria-labelledby={`${optionId}-label`}
              />
              {/* `htmlFor` for the hit target, `aria-labelledby` for the name — see the
                  matching note in ChoiceQuestion. */}
              <label
                htmlFor={optionId}
                id={`${optionId}-label`}
                className="text-sm cursor-pointer"
              >
                {option.label}
              </label>
            </div>
          );
        })}
      </div>
      {error && (
        <p id={errorId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </fieldset>
  );
}
